// packages/core/src/learning/school-modules/coast-fi-slow-fi.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'coast-fi-slow-fi' school. LearningModule,
// surface 'school'.
//   module id   = 'school-coast-fi-slow-fi' (matches exhibit nativeModuleId)
//   homeSchool  = 'coast-fi-slow-fi'
// PROMOTED: five authored lessons (above the divider in the index).
//
// This is a freedom/FI school (bucket 'freedom-lifestyle-paths'), so it sits
// under tier 'freedom-path' / pillar 'freedom-math' rather than the
// investing-strategy 'growth' / 'long-term-investing' mapping.
//
// Placeholder presentational fields (emoji, accentColor, order) carried from
// the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_COAST_FI_SLOW_FI_MODULE: LearningModule = {
	id: 'school-coast-fi-slow-fi',
	title: 'Coast & Slow FI',
	description:
		'The lens that treats freedom as pressure reduction — compounding carrying the future load, protecting life now, changing the job of work, and where calm turns into comfort drift.',
	emoji: '🌅',
	accentColor: '#2bb3a3',
	tier: 'freedom-path',
	pillar: 'freedom-math',
	order: 0,
	surface: 'school',
	homeSchool: 'coast-fi-slow-fi',
	lessons: [
		{
			id: 'school-coast-fi-slow-fi-01',
			title: 'Freedom can start before retirement',
			description:
				'This lesson teaches how Coast and slow FI sees freedom as pressure reduction, not only quitting work.',
			pillar: 'freedom-math',
			schoolsOfThought: ['coast-fi-slow-fi'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['coast-fi-slow-fi', 'pressure-reduction', 'partial-freedom'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'pressure reduction',
				'work optional thinking',
				'partial freedom',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Most people picture financial independence as a finish line. One day you cross it, quit work, and life changes. Coast and slow FI sees something more gradual.',
				},
				{
					type: 'paragraph',
					text: 'This school is paying attention to the stages before full freedom. It asks when money starts lowering pressure, giving you better choices, and making work less desperate.',
				},
				{
					type: 'bullet_list',
					items: [
						'Less pressure matters',
						'Better choices matter',
						'Full retirement is not the only win',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating financial freedom as all or nothing.',
				},
				{
					type: 'example',
					text: 'Someone may not be able to stop working forever, but they may have enough invested and enough runway to leave a toxic job, take a lower stress role, go part time, or avoid panic during a career change.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that partial freedom still needs numbers. Feeling calmer is not the same as being on track.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your freedom plan. The point is to see where pressure starts dropping, then test whether the math supports the feeling.',
				},
				{
					type: 'key_takeaway',
					text: 'Coast and slow FI turns freedom from one finish line into a series of pressure drops.',
				},
			],
			audioScript:
				'Coast and slow FI starts by changing the picture of financial freedom. A lot of people imagine freedom as one huge finish line. You save and invest for years. Then one day the number is big enough, work becomes optional forever, and life finally begins. That version can be motivating, but it can also feel far away, especially for someone starting with debt, normal income, family obligations, or burnout.\n\nCoast and slow FI asks a calmer question: what if freedom starts before full retirement?\n\nThis lens is paying attention to pressure. How much pressure is your future putting on your present? How much money do you need to keep saving? How fragile is your job situation? How desperate would you feel if income changed? How many choices do you have if your life needs a different shape?\n\nFull financial independence means your assets can support your life without needing paid work. Coast FI points to an earlier stage. If enough is already invested for the future, and time has enough room to work, your future retirement load may become lighter. You may still need to earn money for current expenses, but the pressure to save aggressively for later may drop.\n\nThat is powerful because it changes the job of work. Work may stop being the thing that must solve your entire future. It may become the thing that funds your current life while compounding does more of the future work in the background.\n\nSlow FI adds another layer. It says the path matters too. If your plan to reach freedom destroys your health, relationships, energy, and joy for fifteen years, maybe the plan needs a better shape. Slow FI is not an excuse to avoid the numbers. It is a reminder that the point of money is a life you can actually live.\n\nA realistic example helps. Someone cannot quit work completely. But because they saved early, invested steadily, and kept expenses reasonable, they may not need to chase the highest paying job forever. They might take a role with better hours. They might move to a lower stress team. They might start a business slowly. They might take a short break without wrecking the future. That is not full freedom, but it is real freedom.\n\nThe blind spot is that people can turn this into vibes. They feel tired, hear about slow FI, and say, “I just want balance.” That feeling is valid, but the math still matters. Coast FI only works if the current amount, future time, expected contributions, spending, and assumptions make sense. Slow FI only works if the slower pace is honest, not avoidance.\n\nInside Nisk, use this lens to ask where pressure actually drops. Not where you wish it dropped. Where the numbers show more options. The point is not to quit work as fast as possible. The point is to build a life where money slowly stops cornering you.\n\nThe takeaway is simple. Coast and slow FI turns freedom from one finish line into a series of pressure drops. That can make the journey more human, as long as the math stays honest.',
			flashcards: [
				{
					id: 'school-coast-fi-slow-fi-01-fc-1',
					role: 'main_idea',
					front: 'What does Coast FI notice?',
					back: 'Freedom can begin when future pressure starts dropping.',
				},
				{
					id: 'school-coast-fi-slow-fi-01-fc-2',
					role: 'watch_out',
					front: 'What is the all or nothing trap?',
					back: 'Thinking freedom only matters once work can stop forever.',
				},
				{
					id: 'school-coast-fi-slow-fi-01-fc-3',
					role: 'use_it',
					front: 'How do you apply the lens?',
					back: 'Ask where money starts creating more choices before full retirement.',
				},
			],
			applyAction: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
			revision: 1,
		},
		{
			id: 'school-coast-fi-slow-fi-02',
			title: 'Compounding can carry part of the load',
			description:
				'This lesson teaches how Coast and slow FI sees early investing as a way to reduce future pressure.',
			pillar: 'freedom-math',
			schoolsOfThought: ['coast-fi-slow-fi'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'coast-fi-slow-fi',
				'compounding-load-shift',
				'invested-base',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'compounding load shift',
				'future pressure',
				'invested base',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Coast FI is built around one quiet idea: at some point, the money already invested may do more of the future work than new contributions.',
				},
				{
					type: 'paragraph',
					text: 'This school believes time can turn an invested base into a pressure reducer. The earlier the base exists, the more room compounding has to help.',
				},
				{
					type: 'bullet_list',
					items: [
						'The base matters',
						'Time matters',
						'Assumptions matter',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is acting like compounding is automatic rescue.',
				},
				{
					type: 'example',
					text: 'Someone with a meaningful invested base at 30 may not be done building wealth, but future contributions may no longer need to carry the whole retirement burden by themselves.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is assumption risk. Growth rates, inflation, spending, income, and life changes can shift the picture.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this connects to testing your savings rate and future plan. Coast FI is not a mood. It is a projection that needs pressure testing.',
				},
				{
					type: 'key_takeaway',
					text: 'Coast FI works when compounding has enough money, enough time, and honest assumptions.',
				},
			],
			audioScript:
				'Coast FI depends on one quiet but powerful idea: at some point, the money you already invested may start carrying part of the future load.\n\nThis does not mean you are rich. It does not mean you are done working. It does not mean you can stop paying attention. It means the job of your money may have changed. Early in the journey, new contributions do most of the work. You save, invest, repeat, and the balance is still small enough that your monthly actions matter more than investment growth. Later, if the invested base becomes large enough and time is still on your side, compounding may begin to carry more of the future retirement burden.\n\nThat is the Coast FI insight. The point is not that you can stop caring. The point is that future pressure can drop once the invested base has enough time to grow.\n\nThink about two people with the same retirement goal. One starts building an invested base early. The other waits much longer. The first person may reach a point where they still need to earn enough for current life, but they no longer need to save as aggressively for future retirement. The second person may need larger contributions later because there is less time for compounding to help. Same destination, different pressure.\n\nThis is why Coast FI feels emotionally different. It can make the future less heavy. You might still work, but work no longer has to solve every future problem. You might still save, but maybe the required pace is lower. You might still care about income, but maybe you are not trapped chasing the highest possible salary at any personal cost.\n\nBut the blind spot is important. Compounding is not automatic rescue. It depends on assumptions. What return are you assuming? What inflation are you assuming? What spending level are you planning for? What happens if life gets more expensive? What happens if income drops? What happens if markets underperform for a long stretch? What happens if you stop investing too early because the projection felt comforting?\n\nCoast FI can become dangerous when people use it as permission to stop thinking. A projection is not a promise. It is a model. A model can help you think, but it cannot guarantee the future.\n\nInside Nisk, this lesson connects to testing. Look at your savings rate. Look at your invested base. Look at runway. Look at the plan under different assumptions. The goal is not to find the most comforting answer. The goal is to understand what has to be true for pressure to drop.\n\nThis is still general education, not personal advice. The point is to understand the worldview. Coast FI believes that once an invested base exists, time can become a partner. But time only helps if the base is real, the assumptions are reasonable, and the user keeps enough flexibility to adapt.\n\nThe takeaway is simple. Coast FI works when compounding has enough money, enough time, and honest assumptions. Without those three, it becomes a nice story instead of a real pressure reducer.',
			flashcards: [
				{
					id: 'school-coast-fi-slow-fi-02-fc-1',
					role: 'main_idea',
					front: 'What does compounding carry?',
					back: 'It can carry part of the future retirement load over time.',
				},
				{
					id: 'school-coast-fi-slow-fi-02-fc-2',
					role: 'watch_out',
					front: 'What is the compounding trap?',
					back: 'Treating projections like guaranteed rescue.',
				},
				{
					id: 'school-coast-fi-slow-fi-02-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Test the invested base, time, savings rate, and assumptions.',
				},
			],
			applyAction: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
			revision: 1,
		},
		{
			id: 'school-coast-fi-slow-fi-03',
			title: 'Slow FI protects the life now',
			description:
				'This lesson teaches how slow FI weighs freedom against health, relationships, energy, and daily life.',
			pillar: 'freedom-math',
			schoolsOfThought: ['coast-fi-slow-fi'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['coast-fi-slow-fi', 'sustainable-path', 'burnout-tradeoff'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['sustainable path', 'life energy', 'burnout tradeoff'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A freedom plan can become strangely unfriendly to the life it is supposed to improve. Slow FI pushes back on that.',
				},
				{
					type: 'paragraph',
					text: 'This school believes the journey matters because time, health, energy, and relationships are not side notes. They are part of the money decision.',
				},
				{
					type: 'bullet_list',
					items: [
						'Burnout has a cost',
						'Time has a cost',
						'Relationships have a cost',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is sacrificing too much life now for a future version of freedom.',
				},
				{
					type: 'example',
					text: 'Someone could work extreme hours, save aggressively, and reach a number sooner. But if the path damages their health, family, or sense of self, the spreadsheet may be hiding a real cost.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is speed. A more sustainable path may take longer and require clearer choices about what matters most.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to designing the plan around a real human life. The question is not only when you reach freedom, but what the path costs.',
				},
				{
					type: 'key_takeaway',
					text: 'Slow FI asks whether your freedom plan is making your life bigger or smaller.',
				},
			],
			audioScript:
				'Slow FI exists because a financial freedom plan can become weirdly hostile to the life it is supposed to improve.\n\nA person starts with a good goal. They want options. They want less fear. They want freedom from bad jobs, bad timing, and constant money pressure. Then the plan becomes so aggressive that life shrinks. Every dollar becomes a guilt test. Every break feels like weakness. Every purchase feels like failure. Work becomes a grind to escape work. The person is technically moving toward freedom, but emotionally they are living inside a cage.\n\nSlow FI pushes back on that. It says the path matters. Time matters now, not only later. Health matters now. Relationships matter now. Energy matters now. The point of money is not to win a spreadsheet. The point is to build a life with more room to breathe.\n\nThis does not mean spending without thought. It does not mean ignoring the math. It does not mean using balance as an excuse to avoid saving. The serious version of slow FI is not soft. It is honest about the whole cost.\n\nA normal FIRE plan might focus heavily on savings rate and years to independence. Slow FI adds another set of questions. What is this plan doing to your body? What is it doing to your friendships? What is it doing to your partner, family, or future self? Are you becoming more free along the way, or are you postponing your entire life for a number?\n\nImagine someone earning good money in a job that drains them. They could stay for ten years, save aggressively, and maybe reach freedom faster. Or they could take a slightly lower paying role with better hours, keep saving steadily, and reach freedom later. The spreadsheet may prefer the first path. Slow FI asks whether the human being can survive it cleanly.\n\nThat is the tradeoff. A sustainable path may be slower. It may require clearer priorities. It may mean choosing the version of freedom that fits your actual life, not the version that looks most impressive online.\n\nThe blind spot is that slow FI can become too comfortable. A person can say they are protecting life now, while quietly avoiding the hard work of building options. They can lower pressure without replacing it with a real plan. That is not slow FI. That is drift.\n\nInside Nisk, this lens is strongest when paired with numbers. You ask what the aggressive path costs. You ask what the slower path costs. You ask what each version gives back. You test the plan through savings rate, runway, and future scenarios. Then you make the tradeoff visible.\n\nThe takeaway is simple. Slow FI asks whether your freedom plan is making your life bigger or smaller. A good plan should move you toward future freedom without making present life feel like punishment.',
			flashcards: [
				{
					id: 'school-coast-fi-slow-fi-03-fc-1',
					role: 'main_idea',
					front: 'What does slow FI protect?',
					back: 'It protects health, time, relationships, energy, and life now.',
				},
				{
					id: 'school-coast-fi-slow-fi-03-fc-2',
					role: 'watch_out',
					front: 'What is the burnout trap?',
					back: 'Chasing future freedom in a way that makes present life smaller.',
				},
				{
					id: 'school-coast-fi-slow-fi-03-fc-3',
					role: 'use_it',
					front: 'What should you compare?',
					back: 'Compare the faster path against the human cost of reaching it.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name the path cost',
				prompt: 'Write what your current money plan gives you, then write what it costs your health, energy, or relationships.',
			},
			revision: 1,
		},
		{
			id: 'school-coast-fi-slow-fi-04',
			title: 'Enough changes the job of work',
			description:
				'This lesson teaches how Coast and slow FI can shift work from survival pressure toward choice, fit, and flexibility.',
			pillar: 'freedom-math',
			schoolsOfThought: ['coast-fi-slow-fi'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'coast-fi-slow-fi',
				'work-pressure-shift',
				'career-flexibility',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'work pressure shift',
				'enough point',
				'career flexibility',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Work feels different when every paycheck has to carry the entire future. Coast and slow FI notices when that pressure starts to change.',
				},
				{
					type: 'paragraph',
					text: 'This school believes reaching a partial enough point can change the job of work. Work may still matter, but it may not need to solve every future need by itself.',
				},
				{
					type: 'bullet_list',
					items: [
						'Work can fund current life',
						'Investments can support future life',
						'Runway can create better choices',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking the only valuable money milestone is never working again.',
				},
				{
					type: 'example',
					text: 'Someone who has built a base may still work full time, but they might choose a healthier team, negotiate differently, take a career risk, or avoid staying in a job only from fear.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that partial freedom can be fragile. Lifestyle inflation, weak income, or bad assumptions can pull the pressure back.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this connects to building a plan around work you can sustain. The goal is not to hate work less because you are trapped. It is to need less from work over time.',
				},
				{
					type: 'key_takeaway',
					text: 'Coast and slow FI changes work from the whole answer into one part of the plan.',
				},
			],
			audioScript:
				'Coast and slow FI changes how work feels. Not because work disappears immediately, but because work may stop carrying the entire weight of the future.\n\nFor most people, work has too many jobs. It pays rent or housing. It covers food, bills, debt, insurance, family needs, fun, emergencies, and future retirement. That is a lot of pressure to place on one paycheck. When income feels like the only thing standing between you and disaster, work can become scary. Even a good job can feel heavy. A bad job can feel impossible to leave.\n\nCoast and slow FI notices the moment when that starts to shift. If you have built an invested base, if your future retirement load is partly handled by time and compounding, and if you have some runway, work may become less desperate. You may still need income. You may still need discipline. You may still need to make good choices. But work may not need to solve every future problem at once.\n\nThat changes the relationship. Work can become the thing that funds your current life while your earlier investments work in the background for future life. Runway can give you breathing room. Lower expenses can reduce fear. A clearer plan can make career decisions less reactive.\n\nThis does not mean quitting tomorrow. It does not mean becoming careless. It means work starts becoming one part of the plan instead of the whole answer.\n\nA realistic example: someone has saved and invested enough that their future pressure is lower than it used to be. They still work full time. But now they might choose a healthier team over a slightly higher paying toxic role. They might negotiate with less fear. They might take a skill building job that pays less now but fits better later. They might leave a bad manager without feeling like one decision ruins their future.\n\nThat is partial freedom. It is not glamorous, but it is powerful.\n\nThe blind spot is that partial freedom can be fragile. Lifestyle inflation can pull the pressure back. A long break without a plan can damage momentum. Weak assumptions can make the invested base look stronger than it is. A person can mistake temporary comfort for durable freedom.\n\nInside Nisk, this lens connects to designing work around a real plan. You can look at savings rate, runway, expenses, and future scenarios. You can ask what your job needs to provide now and what your investments may handle later. You can test whether a slower path still holds up.\n\nThe deeper question is: what would change if work no longer had to carry everything? Maybe you would set better boundaries. Maybe you would stop chasing status. Maybe you would build skills more calmly. Maybe you would choose a life that needs less money to maintain.\n\nThe takeaway is simple. Coast and slow FI changes work from the whole answer into one part of the plan. That is a meaningful kind of freedom, even before full independence.',
			flashcards: [
				{
					id: 'school-coast-fi-slow-fi-04-fc-1',
					role: 'main_idea',
					front: 'How does enough change work?',
					back: 'Work may fund current life while investments help carry the future.',
				},
				{
					id: 'school-coast-fi-slow-fi-04-fc-2',
					role: 'watch_out',
					front: 'What can pull pressure back?',
					back: 'Lifestyle inflation, weak assumptions, or losing income flexibility.',
				},
				{
					id: 'school-coast-fi-slow-fi-04-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'Ask what work needs to provide now versus later.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-coast-fi-slow-fi-05',
			title: 'Where Coast and slow FI breaks',
			description:
				'This lesson teaches how Coast and slow FI can become drift when comfort replaces honest numbers.',
			pillar: 'freedom-math',
			schoolsOfThought: ['coast-fi-slow-fi'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['coast-fi-slow-fi', 'comfort-drift', 'projection-honesty'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'comfort drift',
				'projection honesty',
				'slow path discipline',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Coast and slow FI breaks when the softer path becomes an excuse to stop looking at the numbers.',
				},
				{
					type: 'paragraph',
					text: 'This school is useful because it lowers pressure and respects life now. But it can go wrong when people call something balance while quietly avoiding reality.',
				},
				{
					type: 'bullet_list',
					items: [
						'Projections can be too rosy',
						'Spending can creep',
						'The plan can lose discipline',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is using slow FI language to avoid hard tradeoffs.',
				},
				{
					type: 'example',
					text: 'Someone says they are coasting, but they have not checked savings rate, retirement assumptions, spending growth, or how long their current plan actually works. The feeling is calm, but the plan may be weak.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is comfort drift. A slower path still needs savings, investing, income, risk awareness, and regular check ins.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should end with honesty. The goal is not maximum intensity. The goal is a plan that is sustainable and real.',
				},
				{
					type: 'key_takeaway',
					text: 'Coast and slow FI works only when the calmer path still has numbers behind it.',
				},
			],
			audioScript:
				'Coast and slow FI is one of the more human freedom lenses, but it has a real failure mode. It can become drift.\n\nThe good version lowers pressure without abandoning responsibility. It says life matters now. It says burnout has a cost. It says full retirement is not the only useful milestone. It says compounding can carry part of the future load if enough is invested early enough. It says work can become less desperate before you are fully financially independent.\n\nThat is healthy. But the weak version sounds similar on the surface while doing something very different. It says, “I am taking the slow path,” when the person has not checked the math. It says, “I am coasting,” when the invested base may not be large enough. It says, “I want balance,” when the person is avoiding a hard spending, income, or savings decision. It says, “I do not want to be extreme,” when the real issue is fear of looking at the numbers.\n\nThat is where Coast and slow FI breaks. It becomes a comforting story instead of a real plan.\n\nA slower path still needs discipline. It needs an honest savings rate. It needs realistic spending. It needs assumptions that are not wildly optimistic. It needs some understanding of inflation, market uncertainty, income changes, and life surprises. It needs regular check ins because a plan that worked at one income, rent level, or family situation may not work forever.\n\nImagine someone who says they reached Coast FI. They reduce saving, upgrade lifestyle, and stop checking the plan. A few years later, expenses are higher, income is less stable, and the original assumptions no longer fit. The problem was not slow FI. The problem was treating a projection like a permanent permission slip.\n\nThis is the core blind spot: comfort drift. When pressure drops, attention can drop too. A person may stop making intentional choices because the plan feels less urgent. But lower pressure should create better judgment, not less judgment.\n\nInside Nisk, this lens should end with honesty. The question is not whether you are intense enough. The question is whether your plan is real. What does your current savings rate do? What is your runway? What assumptions are you relying on? What happens if expenses rise? What happens if income changes? What happens if markets are weaker than expected? What check in would keep you from drifting?\n\nThis is not about shame. It is about protecting the exact thing the school cares about. Coast and slow FI wants a more livable path to freedom. A weak plan will not protect that. Only a sustainable and honest plan can.\n\nThe takeaway is simple. Coast and slow FI works only when the calmer path still has numbers behind it. The goal is not maximum intensity. The goal is a life you can sustain and a plan that can survive reality.',
			flashcards: [
				{
					id: 'school-coast-fi-slow-fi-05-fc-1',
					role: 'main_idea',
					front: 'Where does this lens break?',
					back: 'When slow freedom becomes drift without honest numbers.',
				},
				{
					id: 'school-coast-fi-slow-fi-05-fc-2',
					role: 'watch_out',
					front: 'What is comfort drift?',
					back: 'Lower pressure turning into less attention, weaker habits, or rosy assumptions.',
				},
				{
					id: 'school-coast-fi-slow-fi-05-fc-3',
					role: 'use_it',
					front: 'What keeps it honest?',
					back: 'Regularly check savings rate, runway, spending, income, and assumptions.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
			revision: 1,
		},
	],
};
